// utils/supportContact.js
//
// The one way the app hands a member off to a human.
//
// Every support CTA routes through here so the platform branch (TMA vs PWA) and
// the handle-routing rule live in one place each. Call sites name the INTENT
// (`contactSellersClubAdmin`), never a handle or a URL.

import { SUPPORT_HANDLES, telegramUrl } from '@/constants/support.js';

/**
 * The adapter is imported lazily, on click, and deliberately so.
 *
 * `@/platform/index.js` eagerly constructs PwaAdapter, which statically imports
 * `usePwa` → `virtual:pwa-register/vue`. That virtual module only exists under
 * Vite, so a static import here would drag the whole PWA/service-worker chain into
 * the test graph of every component that merely wants a support link — and fail to
 * resolve. (MembershipStandingCard and OrdersPage both broke on exactly that.)
 *
 * These are user-gesture handlers, so a dynamic import costs nothing anyone can
 * perceive, and consumers stay cheap to test.
 */
async function openChat(handle) {
  const { adapter } = await import('@/platform/index.js');
  adapter.openTelegramChat(telegramUrl(handle));
}

/**
 * Sellers Club admin — the default human.
 *
 * Registration, activation, the first deposit, and any question or clarification.
 * When in doubt about which of these two to call, it is this one.
 *
 * @returns {Promise<void>} Safe to fire and forget from a click handler.
 */
export function contactSellersClubAdmin() {
  return openChat(SUPPORT_HANDLES.SELLERS_CLUB_ADMIN);
}

/**
 * Poolit app admin — technical problems only (app broken, errors, nothing loads).
 *
 * Not for business or money questions, despite what the handle is named.
 *
 * @returns {Promise<void>} Safe to fire and forget from a click handler.
 */
export function contactAppAdmin() {
  return openChat(SUPPORT_HANDLES.APP_ADMIN);
}
